import { useState } from "react";
import { DropdownNestedItem } from "../types/genereal";

type Props = {
  items: DropdownNestedItem[];
  selected: DropdownNestedItem[];
  setSelected: React.Dispatch<React.SetStateAction<DropdownNestedItem[]>>;
};

export const useNestedDropdown = ({ items, selected, setSelected }: Props) => {
  const [internalSelected, setInternalSelected] = useState(selected);

  const selectedCount = internalSelected.reduce(
    (acc, curr) => acc + curr.nestedItems.length,
    0,
  );

  const checkParent = (parentName: string) => {
    const originalParent = items.find((item) => item.name === parentName);
    if (!originalParent)
      return { isSelected: false, nestedTotal: 0, nestedSelected: 0 };

    const nestedTotal = originalParent.nestedItems.length;

    const currentParent = internalSelected.find(
      (item) => item.name === parentName,
    );
    const nestedSelected = currentParent?.nestedItems.length || 0;

    const isAllChildrenSelected =
      nestedTotal > 0 && nestedSelected === nestedTotal;
    const isManuallySelected = currentParent?.selected === true;

    // If item has no nestedItems, treat manual selection as the only way to select
    const isSelected =
      (nestedTotal === 0 && isManuallySelected) ||
      (nestedTotal > 0 && (isAllChildrenSelected || isManuallySelected));

    return { isSelected, nestedTotal, nestedSelected };
  };

  const selectedParents = selected.reduce<
    { name: string; nestedTotal: number }[]
  >((acc, parent) => {
    const { isSelected, nestedTotal } = checkParent(parent.name);
    if (isSelected) {
      acc.push({ name: parent.name, nestedTotal });
    }
    return acc;
  }, []);

  const nestedItems = selected
    .filter((parent) => !checkParent(parent.name).isSelected)
    .flatMap((parent) => parent.nestedItems.map((child) => child));

  const toggleParent = (parent: DropdownNestedItem) => {
    const originalParent = items.find((item) => item.name === parent.name);
    if (!originalParent) return;

    const nestedTotal = originalParent.nestedItems.length;

    const existingParent = internalSelected.find(
      (item) => item.name === parent.name,
    );

    const isFullySelected =
      nestedTotal === 0
        ? !!existingParent?.selected
        : existingParent?.nestedItems.length === nestedTotal;

    setInternalSelected((prev) => {
      if (isFullySelected) {
        return prev.filter((item) => item.name !== parent.name);
      } else {
        const updated = prev.filter((item) => item.name !== parent.name);
        return [
          ...updated,
          {
            ...parent,
            nestedItems: originalParent.nestedItems,
            selected: true,
          },
        ];
      }
    });
  };

  const removeParent = (parentName: string) => {
    const updated = selected.filter((parent) => parent.name !== parentName);

    setInternalSelected(updated);
    setSelected(updated);
  };

  const getSelectedChildCount = (parentName: string) => {
    const existingParent = internalSelected.find(
      (item) => item.name === parentName,
    );
    return existingParent ? existingParent.nestedItems.length : 0;
  };

  const removeChild = (child: string) => {
    const updated = selected
      .map((item) => ({
        ...item,
        nestedItems: item.nestedItems.filter((s) => s !== child),
      }))
      .filter((item) => item.selected || item.nestedItems.length > 0);

    setInternalSelected(updated);
    setSelected(updated);
  };

  const toggleChild = (parentName: string, child: string) => {
    const parent = items.find((item) => item.name === parentName);
    if (!parent || parent.nestedItems.length === 0) return;

    const isCurrentlySelected = internalSelected.some((item) =>
      item.nestedItems.includes(child),
    );

    let updated: DropdownNestedItem[] = [];

    if (isCurrentlySelected) {
      // Remove the child from the appropriate parent
      updated = internalSelected
        .map((item) => {
          if (item.name !== parentName) return item;

          const newNestedItems = item.nestedItems.filter((s) => s !== child);
          return {
            ...item,
            nestedItems: newNestedItems,
            selected: false, // Clear manual flag if changing children
          };
        })
        .filter((item) => item.nestedItems.length > 0);
    } else {
      let found = false;
      updated = internalSelected.map((item) => {
        if (item.name !== parentName) return item;

        found = true;
        // Add the child to the existing parent
        const newItems = [...item.nestedItems, child];
        return {
          ...item,
          nestedItems: newItems,
          selected: false,
        };
      });

      // If parent wasn't already in internalSelected, add it
      if (!found) {
        updated.push({
          name: parentName,
          nestedItems: [child],
          selected: false,
        });
      }
    }

    setInternalSelected(updated);
  };

  const isChildSelected = (child: string) =>
    internalSelected.some((item) => item.nestedItems.includes(child));

  return {
    toggleParent,
    removeParent,
    removeChild,
    toggleChild,
    isChildSelected,
    checkParent,
    selectedParents,
    nestedItems,
    selectedCount,
    internalSelected,
    setInternalSelected,
    getSelectedChildCount,
  };
};
